// Return-to contract (ADR 0052): apps send a `redirect_uri` when bouncing the user to the portal for
// login; the portal echoes it back afterwards (e.g. store checkout returns to the exact offer). Only
// same-platform origins are allowed, so the portal can't be used as an open redirector.

// Private LAN IPv4 ranges — used only for on-device dev testing over the local network.
function isPrivateHost(host: string): boolean {
  return /^10\./.test(host) || /^192\.168\./.test(host) || /^172\.(1[6-9]|2\d|3[01])\./.test(host)
}

export function isAllowedReturnTo(url: string): boolean {
  try {
    const target = new URL(url, window.location.origin)
    if (target.origin === window.location.origin) return true
    const host = target.hostname
    const isLocal = host === 'localhost' || host === '127.0.0.1'
    // #490: a public return target must be https. Without this an `http://…polux.weapps.net` returnTo was
    // accepted, and the sign-in status would be handed back over plaintext. Dev hosts stay exempt.
    if (!isLocal && !isPrivateHost(host) && target.protocol !== 'https:') return false
    if (isLocal) return true
    if (host === 'polux.weapps.net' || host.endsWith('.polux.weapps.net')) return true
    // On-device dev: when the portal itself runs on a LAN IP, allow a sibling dev app on the same private
    // network (different port → different origin) as a return target. Gated on the portal being on a
    // private host so the deployed portal never becomes an open redirector to arbitrary private IPs.
    if (isPrivateHost(window.location.hostname) && isPrivateHost(host)) return true
    return false
  } catch {
    return false
  }
}

export function resolveReturnTo(searchParams: URLSearchParams, fallback: string): string {
  const raw = searchParams.get('redirect_uri') || searchParams.get('returnTo')
  if (raw && isAllowedReturnTo(raw)) return raw
  return fallback
}

export type ReturnStatus = 'success' | 'cancelled' | 'error'

// Accounts Return Callback (ADR 0052): hand the caller back a STATUS only — never a token. The session
// itself rides the Audience Bridge cookie for same-parent-domain callers. Returns the URL to navigate
// to (with `?auth=<status>` appended) for external callers, or null for an in-app destination.
export function buildReturnUrl(returnTo: string, status: ReturnStatus): string | null {
  if (!returnTo.startsWith('http')) return null
  try {
    const url = new URL(returnTo)
    url.searchParams.set('auth', status)
    return url.toString()
  } catch {
    return null
  }
}

// Provider-reported cancellation: the person tapped Cancel on Apple's sheet or Google's consent screen
// and Cognito bounced it to /callback as `?error=`. An Abandoned Sign-in is a CHOICE, not a failure (ADR
// 0137 decision 3), so these codes are never shown as an error; anything else the provider reports is.
const PROVIDER_CANCEL_CODES = new Set(['access_denied', 'user_cancelled_authorize', 'user_cancelled'])
export function isProviderCancel(error: string | null | undefined): boolean {
  return !!error && PROVIDER_CANCEL_CODES.has(error.trim().toLowerCase())
}
